import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { Bucket, HIGH_HOURS, LOW_HOURS, WorklogRow, hoursBucket, isWeekend, personStats } from './model';
import * as fmt from '../../lib/format';

interface Props {
  row: WorklogRow;
  days: string[];
  today: string;
  year: number;
  month: number;
  /** Pressing a day opens what was logged on it. */
  onOpenDay: (day: string) => void;
}

interface TagHours {
  log_type: string;
  minutes: number;
  hours: number;
}

const BAR: Record<Bucket, string> = { none: 'bg-gray-200', lo: 'bg-red-400', ok: 'bg-emerald-500', hi: 'bg-amber-400' };
const CHART_H = 96;

// One person's month: how the days went, where the time went, and the weekdays with nothing logged.
export default function PersonPanel({ row, days, today, year, month, onOpenDay }: Props) {
  const stats = personStats(row, days, today);
  const hoursOn = (d: string) => (row.dailyMinutes?.[d] || 0) / 60;
  const top = Math.max(HIGH_HOURS, ...days.map(hoursOn));

  const [tags, setTags] = useState<{ status: 'loading' } | { status: 'ready'; rows: TagHours[] } | { status: 'error' }>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    setTags({ status: 'loading' });
    apiService
      .getUserMonthLogType(row.accountId, year, month)
      .then((res: any) => {
        if (cancelled) return;
        setTags(res?.success && Array.isArray(res.data) ? { status: 'ready', rows: res.data } : { status: 'error' });
      })
      .catch(() => !cancelled && setTags({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [row.accountId, year, month]);

  const tagTotal = tags.status === 'ready' ? tags.rows.reduce((n, t) => n + t.minutes, 0) : 0;
  const lineAt = (h: number) => `${(h / top) * 100}%`;

  return (
    <div className="space-y-6 text-sm text-gray-700">
      <div>
        <div className="text-xs text-gray-500">{[row.userRole, row.managerName ? `Manager: ${row.managerName}` : null].filter(Boolean).join(' · ') || 'No role or manager on record'}</div>
        <dl className="mt-3 grid grid-cols-3 gap-3">
          {[
            ['Hours', fmt.hoursBare(stats.hours)],
            ['Days logged', String(stats.loggedDays)],
            ['Average a day', fmt.hours(stats.averagePerLoggedDay)],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl bg-gray-50 px-3 py-2 ring-1 ring-gray-200/60">
              <dt className="text-[0.6875rem] font-medium text-gray-500">{label}</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <section aria-label="Day by day">
        <h4 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Day by day</h4>
        <div className="relative mt-3" style={{ height: CHART_H }}>
          {[LOW_HOURS, HIGH_HOURS].map((h) => (
            <div key={h} className="pointer-events-none absolute inset-x-0 border-t border-dashed border-gray-200" style={{ bottom: lineAt(h) }}>
              <span className="absolute -top-2 right-0 bg-white pl-1 text-[0.6875rem] text-gray-500">{h} h</span>
            </div>
          ))}
          <div className="absolute inset-0 flex items-end gap-[3px]">
            {days.map((d) => {
              const h = hoursOn(d);
              const b = hoursBucket(h);
              const label = `${fmt.weekdayDay(d)}: ${b === 'none' ? 'nothing logged' : fmt.hours(h)}`;
              return (
                <button
                  key={d}
                  type="button"
                  disabled={b === 'none'}
                  onClick={() => onOpenDay(d)}
                  title={label}
                  aria-label={b === 'none' ? label : `${label}. Show what was logged.`}
                  className="group flex h-full min-w-0 flex-1 items-end rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-default"
                >
                  <span
                    className={`block w-full rounded-t-sm transition-opacity motion-reduce:transition-none ${BAR[b]} ${isWeekend(d) ? 'opacity-50' : ''} ${b === 'none' ? '' : 'group-hover:opacity-80'}`}
                    style={{ height: b === 'none' ? 3 : `${Math.max(4, (h / top) * 100)}%` }}
                  />
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-1 flex gap-[3px] text-[0.6875rem] tabular-nums text-gray-500" aria-hidden>
          {days.map((d, i) => (
            <span key={d} className="min-w-0 flex-1 text-center">
              {i === 0 || Number(d.slice(8, 10)) % 5 === 0 || i === days.length - 1 ? Number(d.slice(8, 10)) : ''}
            </span>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">Press a day to see what was logged. Weekends are lighter.</p>
      </section>

      <section aria-label="Where the time went">
        <h4 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Where the time went</h4>
        {tags.status === 'loading' ? (
          <div className="mt-3 space-y-2" role="status" aria-label="Loading the tags">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-4 animate-pulse rounded bg-gray-100 motion-reduce:animate-none" />
            ))}
          </div>
        ) : tags.status === 'error' ? (
          <p className="mt-2 text-xs text-rose-700" role="alert">The tags could not be loaded.</p>
        ) : tags.rows.length === 0 ? (
          <p className="mt-2 text-xs text-gray-500">No time logged this month.</p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {tags.rows.map((t) => {
              const pct = tagTotal > 0 ? (t.minutes / tagTotal) * 100 : 0;
              return (
                <li key={t.log_type} className="grid grid-cols-[6.5rem_minmax(0,1fr)_5.75rem] items-center gap-2 text-xs">
                  <span className="truncate text-gray-700">{t.log_type === '[]' ? 'Untagged' : t.log_type}</span>
                  <span className="h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden>
                    <span className="block h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="whitespace-nowrap text-right tabular-nums text-gray-500">
                    {fmt.hours(t.hours)} · {fmt.percent(pct)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-label="Weekdays with no log">
        <h4 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">
          Weekdays with no log · {stats.missingDays.length}
        </h4>
        {stats.missingDays.length === 0 ? (
          <p className="mt-2 text-xs text-gray-500">None so far this month.</p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {stats.missingDays.map((d) => (
              <li key={d} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">
                {fmt.weekdayDay(d)}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-xs text-gray-500">Leave and public holidays are not known here, so they show up as days with no log.</p>
      </section>
    </div>
  );
}
